// Fetch helpers for manually assigning/unassigning indoor, outdoor, and
// furnace products on an AHRI system (AHRI Systems Review > Assign Models).
import { API_BASE_URL } from '../../../config';
import { searchProductsSolr } from '../../../services/configurableProductApi';

const request = async (path, { method = 'GET', body, getAuthHeaders } = {}) => {
  const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
  const resp = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      ...headers,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(String(data?.detail || resp.statusText || `Request failed (${resp.status})`));
  }
  return data;
};

export const getAhriSystemComponents = (ahriId, getAuthHeaders) =>
  request(`/api/v1/ahri/systems/${encodeURIComponent(ahriId)}/components`, { getAuthHeaders });

export const assignAhriProduct = (ahriId, field, productCode, getAuthHeaders) =>
  request(`/api/v1/ahri/systems/${encodeURIComponent(ahriId)}/assign`, {
    method: 'POST',
    body: { field, product_code: productCode },
    getAuthHeaders,
  });

export const unassignAhriProduct = (ahriId, field, productCode, getAuthHeaders) =>
  request(`/api/v1/ahri/systems/${encodeURIComponent(ahriId)}/unassign`, {
    method: 'POST',
    body: { field, product_code: productCode },
    getAuthHeaders,
  });

// Best-effort keyword hints per AHRI field, mirroring
// `ahri_match_scoring.DEFAULT_FIELD_KEYWORDS` on the backend. There is no
// strict product category enum in this codebase, so these are used only to
// rank/group search results client-side — never as a hard server-side filter
// that could return zero results for a correctly-matched product.
export const FIELD_CATEGORY_KEYWORDS = {
  indoorModelNumber: ['coil', 'evaporator', 'air handler', 'fan coil', 'cassette', 'indoor'],
  outdoorModelNumber: ['condenser', 'heat pump', 'condensing unit', 'outdoor', 'ac unit', 'split system'],
  furnaceModelNumber: ['furnace', 'gas furnace', 'oil furnace'],
};

export const FIELD_LABELS = {
  indoorModelNumber: 'Indoor',
  outdoorModelNumber: 'Outdoor',
  furnaceModelNumber: 'Furnace',
};

const matchesFieldKeywords = (doc, field) => {
  const keywords = FIELD_CATEGORY_KEYWORDS[field] || [];
  if (!keywords.length) return false;
  const haystack = `${doc?.category || ''} ${doc?.name || ''}`.toLowerCase();
  return keywords.some((kw) => haystack.includes(kw));
};

// Searches the product catalog and returns results with on-category matches
// (by keyword hint) ranked first, falling back to the full unfiltered set
// when nothing matches the field's keywords.
export const searchProductsForField = async (field, query, getAuthHeaders, page = 1) => {
  const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
  const data = await searchProductsSolr({ query, page, page_size: 20 }, headers);
  const docs = Array.isArray(data?.docs) ? data.docs : [];
  const onCategory = docs.filter((d) => matchesFieldKeywords(d, field));
  const rest = docs.filter((d) => !onCategory.includes(d));
  return {
    docs: onCategory.length ? [...onCategory, ...rest] : docs,
    onCategoryCount: onCategory.length,
    totalCount: docs.length,
  };
};
